import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Trophy, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { useAccounts } from "@/context/AccountContext";
import StartChallengeDialog from "@/components/StartChallengeDialog";
import ChallengeRing from "@/components/ChallengeRing";

/**
 * /prop-challenge (no account_id)
 *
 * Landing page for the sidebar link. Behaviour:
 *  - If the ACTIVE account has an active/latest challenge → redirect to /prop-challenge/{id}
 *  - Else if ANY account has a challenge → list them and redirect on click
 *  - Else show a "Start your first challenge" empty state
 */
export default function PropChallengeIndex() {
  const { accounts, activeAccountId } = useAccounts();
  const navigate = useNavigate();
  const [challenges, setChallenges] = useState({});
  const [loading, setLoading] = useState(true);
  const [startFor, setStartFor] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!accounts?.length) { setLoading(false); return; }
      setLoading(true);
      const map = {};
      await Promise.all(
        accounts.map(async (a) => {
          try {
            const r = await api.get(`/accounts/${a.id}/challenge`);
            if (r.data?.challenge) map[a.id] = r.data.challenge;
          } catch { /* silent */ }
        }),
      );
      if (cancelled) return;
      setChallenges(map);
      setLoading(false);
      // Auto-navigate: prefer the active account's challenge, else the first challenge found.
      const preferred = activeAccountId && map[activeAccountId] ? activeAccountId : Object.keys(map)[0];
      if (preferred) navigate(`/prop-challenge/${preferred}`, { replace: true });
    })();
    return () => { cancelled = true; };
  }, [accounts, activeAccountId, navigate]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-[#9ca3af] text-sm font-mono" data-testid="prop-index-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading challenges…
      </div>
    );
  }

  const withChallenge = accounts.filter((a) => challenges[a.id]);
  const withoutChallenge = accounts.filter((a) => !challenges[a.id]);

  return (
    <div className="space-y-6" data-testid="prop-index-page">
      <div>
        <div className="vx-label">Prop Firm Concierge</div>
        <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tighter mt-1">
          Challenges
        </h1>
        <p className="text-sm text-[#9ca3af] mt-2 max-w-2xl">
          Track FTMO, MyForexFunds, The5ers, E8 Funding and more. Pick a preset, we auto-configure your risk guardrails,
          count trading days, and coach you through it.
        </p>
      </div>

      {withChallenge.length === 0 ? (
        <div className="vx-card p-8 text-center" data-testid="prop-index-empty">
          <Trophy className="w-10 h-10 text-[#f59e0b] mx-auto mb-3" strokeWidth={1.5} />
          <div className="font-heading text-xl font-black tracking-tight">No active challenges yet</div>
          <p className="text-sm text-[#9ca3af] mt-2 mb-6 max-w-md mx-auto">
            Pick a firm preset, and we&apos;ll auto-configure daily-loss and drawdown limits to match.
          </p>
          {accounts?.length > 0 && (
            <button
              onClick={() => setStartFor(activeAccountId || accounts[0].id)}
              data-testid="prop-index-start-btn"
              className="h-10 px-5 rounded bg-[#f59e0b] hover:bg-[#d97706] text-black font-semibold text-sm inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> Start a Challenge
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="prop-index-grid">
          {withChallenge.map((a) => {
            const c = challenges[a.id];
            return (
              <button
                key={a.id}
                onClick={() => navigate(`/prop-challenge/${a.id}`)}
                data-testid={`prop-index-card-${a.id}`}
                className="vx-card p-4 text-left hover:bg-[#1f2937]/40 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <ChallengeRing
                    progressPct={c.current_pnl_pct}
                    targetPct={c.profit_target_pct}
                    drawdownPct={c.current_drawdown_pct}
                    maxDrawdownPct={c.max_total_drawdown_pct}
                    daysRemaining={c.days_remaining}
                    status={c.status}
                    size={72}
                    strokeWidth={7}
                    label={c.firm_name}
                  />
                  <div className="min-w-0">
                    <div className="font-heading font-black text-lg tracking-tight truncate">{a.account_name}</div>
                    <div className="text-[10px] font-mono uppercase text-[#9ca3af] tracking-widest">
                      {c.firm_name} · {c.challenge_type?.replace("_", " ")}
                    </div>
                    <div className="mt-2 text-xs font-mono">
                      <span className="text-[#9ca3af]">Progress: </span>
                      <span className="text-[#e5e7eb] font-bold">{Number(c.current_pnl_pct || 0).toFixed(2)}%</span>
                      <span className="text-[#6b7280]"> / {c.profit_target_pct}%</span>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {withoutChallenge.length > 0 && (
        <div className="vx-card p-4" data-testid="prop-index-add-more">
          <div className="vx-label mb-2">Accounts without a challenge</div>
          <div className="flex flex-wrap gap-2">
            {withoutChallenge.map((a) => (
              <button
                key={a.id}
                onClick={() => setStartFor(a.id)}
                data-testid={`prop-index-start-account-${a.id}`}
                className="h-8 px-3 rounded border border-[#f59e0b]/40 text-[#f59e0b] hover:bg-[#f59e0b]/10 text-xs font-mono inline-flex items-center gap-1"
              >
                <Plus className="w-3 h-3" /> {a.account_name}
              </button>
            ))}
          </div>
        </div>
      )}

      <StartChallengeDialog
        open={!!startFor}
        onClose={() => setStartFor(null)}
        accountId={startFor}
        onCreated={(c) => {
          setStartFor(null);
          if (c?.account_id) navigate(`/prop-challenge/${c.account_id}`);
        }}
      />
    </div>
  );
}
